import { Component, forwardRef, Input,  OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR  } from '@angular/forms';
import { format, isValid } from 'date-fns';
import { capitalize, startCase } from 'lodash';
import { Popover } from 'primeng/popover';
import { DatesService } from './dates.service';
import { DateRange, DynamicRange, DateRangeValue, FixedDateRange } from './types';
import { parseDate } from '../../utils/date.util';

const DATERANGE_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DateRangePickerComponent),
  multi: true,
};

@Component({
  selector: 'date-range-picker',
  styleUrls: ['./date-range-picker.component.less'],
  templateUrl: './date-range-picker.component.html',
  providers: [DATERANGE_PICKER_VALUE_ACCESSOR],
  standalone: false
})
export class DateRangePickerComponent implements ControlValueAccessor, OnChanges {
  @ViewChild('overlayPanel') public overlayPanel: Popover;

  @Input() fixed: boolean;
  @Input() format = 'MMM d, yyyy';
  @Input() requireApply = false;
  @Input() onlyFixed = false;
  @Input() readonly = false;
  @Input() infoMsg: string;
  @Input() min: Date;
  @Input() max: Date;
  @Input() firstDayOfWeek: string;
  @Input() ranges: DynamicRange[] | Record<string, Date[]> = [
    { name: 'yesterday' },
    { name: 'last', num: 7, unit: 'day' },
    { name: 'last', num: 30, unit: 'day' },
    { name: 'last', num: 1, unit: 'week' },
    { name: 'last', num: 1, unit: 'month' },
    { name: 'custom' },
    { name: 'fixed' },
  ];

  public computedRanges: DynamicRange[] = <any> this.ranges;
  public isDisabled: boolean;
  public startDate: Date;
  public endDate: Date;
  public formattedValue: string;
  public formattedStartDate: string;
  public formattedEndDate: string;
  public invalidDatesMsg: string;
  public customInterval: DynamicRange = {
    name: 'this',
    num: 1,
    unit: 'week',
    offset: 0,
  };

  public selectedDynamic?: DynamicRange;
  public customIntervalUnit = { value: 'week', name: 'Week' };
  public availableUnits = [
    { value: 'day', name: 'Day' },
    { value: 'week', name: 'Week' },
    { value: 'month', name: 'Month' },
    { value: 'quarter', name: 'Quarter' },
    { value: 'year', name: 'Year' },
  ];

  public availableOffsetOptions = [
    { value: 0, label: 'Today' },
    { value: -1, label: 'Yesterday' },
  ];

  private onChangeCallback: any;
  private onTouchedCallback: any;
  private value: DateRangeValue;
  private activeValue: DateRangeValue;

  constructor(
    private datesService: DatesService,
  ) {
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.ranges) {
      if (Array.isArray(this.ranges)) {
        this.computedRanges = this.ranges;
      } else {
        this.computedRanges = (Object.keys(this.ranges) as string[]).map(key => this.parseLegacyDynamicRange(key)).filter(Boolean) as DynamicRange[];
        if (!this.computedRanges.find((range) => range.name === 'fixed')) {
          this.computedRanges.push({ title: 'Fixed Range', name: 'fixed' });
        }
      }
      this.selectMatchingDynamic(false);
    }
    if ('firstDayOfWeek' in changes) {
      if (this.activeValue && this.activeValue.type === 'dynamic' ) {
        this.setDynamicRange(this.activeValue.dynamic);
      }
    }
  }

  public writeValue(datesValue: DateRangeValue): void {
    this.setValues(datesValue);
    this.value = this.activeValue;
    this.setFormattedValue();
  }

  public updateDates(dates: DateRange) {
    this.setFixed(dates);
  }

  public updateDate(which: 'start' | 'end', event: Event) {
    const dateStr = (event.target as HTMLInputElement).value;

    if (dateStr) {
      const parsed = parseDate(dateStr);
      const before = this.activeValue as FixedDateRange;
      this.setFixed({
        startDate: (which === 'start') ? parsed : before.startDate,
        endDate: (which === 'start') ? before.endDate : parsed,
      });
    }
  }

  public cancel() {
    this.setValues(this.value);
    this.close();
  }

  public apply(notifyParent: boolean = true) {
    const valueChanged = JSON.stringify(this.value) !== JSON.stringify(this.activeValue);
    if (valueChanged) {
      this.value = this.activeValue;
      this.setFormattedValue();
    }
    this.close();

    if (valueChanged && this.onChangeCallback && notifyParent) {
      this.onChangeCallback(this.value);
    }
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  public label(value: DynamicRange): string {
    if (value && (value.title || value.name)) {
      let label;

      if (value.title) {
        label = value.title;
      } else {
        label = startCase(value.name);

        if (value.num > 1) {
          label += ` ${value.num}`;
        }

        if (value.unit) {
          label += ` ${capitalize(value.unit)}`;
          if (value.num > 1) {
            label += 's';
          }
        }
      }

      if (value.toDate && this.supportsToDate(value.name, value.unit)) {
        if (value.offset !== undefined) {
          if (value.offset === 0) {
            label = label.concat(' to today');
          } else if (value.offset === -1) {
            label = label.concat(' to yesterday');
          }
        } else {
          label = label.concat(' to today');
        }
      }
      return label;
    } else {
      return '';
    }
  }

  public updateCustomInterval(option?: Partial<DynamicRange>) {
    if (option?.num !== undefined && option.num !== null) {
      if (option.num < 1) {
        option = Object.assign({}, option, { num: 1 });
      } else if (option.num > 999) {
        option = Object.assign({}, option, { num: 999 });
      }
    }
    this.customInterval = { ...this.customInterval, ...option };
    this.setCustomIntervalUnit();
    this.setDynamicRange({ name: 'custom' });
  }

  public setDynamicRange(dynamic: DynamicRange, notifyParent: boolean = true) {
    if (dynamic.name === 'fixed') {
      this.setFixed(this.datesService.parseValue(this.activeValue, this.firstDayOfWeek ? Number(this.firstDayOfWeek) : undefined));
      return;
    }
    const forceCustom = (dynamic.name === 'custom');
    if (forceCustom) { dynamic = this.customInterval; }
    const range = this.label(dynamic);
    const { startDate, endDate } = this.datesService.parse(dynamic, this.firstDayOfWeek ? Number(this.firstDayOfWeek) : undefined);
    this.startDate = startDate;
    this.endDate = endDate;
    this.activeValue = { range, type: 'dynamic', dynamic: dynamic };
    this.setFormattedDates();
    this.selectMatchingDynamic(forceCustom);
    if (!this.requireApply && !forceCustom) {
      this.apply(notifyParent);
    }
  }

  private supportsToDate(name, unit) {
    return name === 'this' && (unit === 'month' || unit === 'quarter' || unit === 'year' ) ? true : false;
  }

  private setFormattedDates() {
    if (this.activeValue) {
      const { startDate, endDate } = this.datesService.parseValue(this.activeValue, this.firstDayOfWeek ? Number(this.firstDayOfWeek) : undefined);

      if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) {
        this.formattedStartDate = this.formattedEndDate = '';
        return;
      }

      this.formattedStartDate = format(startDate, this.format);
      this.formattedEndDate = format(endDate, this.format);
    } else {
      this.formattedEndDate = this.formattedStartDate = '';
    }
    this.updateInvalidDatesMsg();
  }

  private setFormattedValue() {
    if (this.value) {
      const dynamic: DynamicRange = this.value['dynamic'];
      if (dynamic && dynamic.name && dynamic.name !== 'fixed' && dynamic.name !== 'custom') {
        this.formattedValue = this.label(dynamic);
      } else {
        this.formattedValue = `${this.formattedStartDate} - ${this.formattedEndDate}`;
      }
    } else {
      this.formattedValue = '';
    }
  }

  private close() {
    if (this.overlayPanel) {
      this.overlayPanel.hide();
    }

    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  private setCustomIntervalUnit() {
    const plural = (this.customInterval.num || 1) > 1;
    const ending = plural ? 's' : '';
    const unit = this.customInterval.unit;
    const dropdownValue = { value: unit, name: capitalize(unit) + ending };
    if (plural !== this.customIntervalUnit.name.endsWith('s')) {
      this.customIntervalUnit = { value: '', name: '' };
      this.availableUnits = this.availableUnits.map(available => ({ value: available.value, name: available.name.replace(/s?$/, ending) }));
      setTimeout(() => { this.customIntervalUnit = dropdownValue; }, 0);
    } else if (unit !== this.customIntervalUnit.value) {
      this.customIntervalUnit = dropdownValue;
    }
  }

  private setFixed(dates: DateRange) {
    const { startDate, endDate } = dates;
    this.activeValue = { type: 'fixed', startDate, endDate };
    this.startDate = startDate;
    this.endDate = endDate;
    this.setFormattedDates();
    this.selectMatchingDynamic(false);
    this.activeValue.range = this.label(this.selectedDynamic);
  }

  private selectMatchingDynamic(forceCustom: boolean) {
    if (this.activeValue) {
      const option = <DynamicRange> (this.activeValue && this.activeValue['dynamic']);
      if (option) {
        this.selectedDynamic = !forceCustom && this.computedRanges.find(dynamic =>
          option.name === dynamic.name && (option.num || 1) === (dynamic.num || 1) && option.unit === dynamic.unit,
        );
        this.selectedDynamic = this.selectedDynamic || this.computedRanges.find((dynamic) => dynamic.name === 'custom');
        if (!forceCustom && this.selectedDynamic && this.selectedDynamic.name === 'custom') {
          this.customInterval = option;
          this.setCustomIntervalUnit();
        }
      } else {
        this.selectedDynamic = this.computedRanges.find((dynamic) => dynamic.name === 'fixed');
      }
    } else {
      this.selectedDynamic = null;
    }
  }

  private setValues(newValue: DateRangeValue) {
    if (!newValue) {
      this.activeValue = this.value = null;
      this.setFormattedDates();
      this.setFormattedValue();
    } else if (newValue['startDate'] && (this.onlyFixed || !newValue['dynamic'])) {
      this.setFixed(<FixedDateRange> newValue);
    } else if (!this.onlyFixed) {
      if (newValue['dynamic']) {
        if (newValue['dynamic'].offset === undefined) {
          newValue['dynamic'].offset = this.availableOffsetOptions[0].value;
        }
        this.setDynamicRange(newValue['dynamic'], false);
      } else {
        const dynamic = this.parseLegacyDynamicRange(newValue.range);
        if (dynamic) {
          this.setDynamicRange(dynamic, false);
        } else {
          this.activeValue = this.value = null;
          this.setFormattedDates();
          this.setFormattedValue();
        }
      }
    }
  }

  private updateInvalidDatesMsg() {
    if (this.startDate && this.endDate && !this.datesService.isValid(this.startDate, this.endDate)) {
      this.invalidDatesMsg = 'Report includes finalized data through yesterday. No finalized data is available yet for the current period.';
    } else {
      this.invalidDatesMsg = '';
    }
  }

  private parseLegacyDynamicRange(title: string): DynamicRange | undefined {
    if (!title) return;
    const name = title.toLowerCase();
    if (name === 'yesterday') {
      return { title, name };
    } else {
      const match = /^\s*(this|last)\s+(\d+\s+)?(day|week|month|year)s?\s*$/.exec(name);
      if (match) {
        return { title, name: match[1], num: parseInt(match[2] || '1'), unit: <any> match[3] };
      }
    }
  }
}
